import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Mail, Download, Trash2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';

const AdminNewsletterPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null); // id being deleted

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/newsletter/list`, { params: { key } });
      setSubscribers(res.data.subscribers || []);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(err?.response?.status === 403
        ? 'Onjuist wachtwoord — probeer opnieuw.'
        : 'Kan de lijst niet ophalen. Controleer je verbinding.');
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) fetchList(authKey);
  }, [authKey, fetchList]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthKey('');
    setKeyInput('');
    setSubscribers([]);
  };

  const handleDelete = async (sub) => {
    const ok = window.confirm(`Weet je zeker dat je ${sub.email} wil verwijderen? Deze actie kan niet ongedaan worden gemaakt.`);
    if (!ok) return;
    setDeleting(sub.id);
    try {
      await axios.delete(`${API}/newsletter/subscriber/${sub.id}`, { params: { key: authKey } });
      setSubscribers((prev) => prev.filter((s) => s.id !== sub.id));
    } catch (err) {
      alert('Verwijderen mislukt. Probeer opnieuw.');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadCsv = () => {
    window.open(`${API}/newsletter/export?key=${encodeURIComponent(authKey)}`, '_blank');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  // ---- Login screen ----
  if (!authKey) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="admin-login">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-3 rounded-full mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Beheer</h1>
            <p className="text-sm text-gray-500 text-center">
              Voer je beheerderswachtwoord in om de nieuwsbrief-inschrijvingen te bekijken.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Wachtwoord"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="admin-key-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !keyInput.trim()}
              className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
              data-testid="admin-login-button"
            >
              {loading ? 'Bezig...' : 'Inloggen'}
            </button>
            {error && (
              <p className="text-sm text-red-600 text-center" data-testid="admin-error">{error}</p>
            )}
          </form>
        </div>
      </div>
    );
  }

  // ---- Authenticated dashboard ----
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Nieuwsbrief-inschrijvingen</h1>
            <p className="text-sm text-gray-500 mt-1">Beheer je abonnees in één overzicht.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchList(authKey)}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Vernieuwen
            </button>
            <button
              onClick={handleDownloadCsv}
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700"
              data-testid="admin-download-csv"
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 px-3"
              data-testid="admin-logout"
            >
              Uitloggen
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 text-gray-500 text-sm mb-1">
              <Mail className="w-4 h-4" /> Totaal inschrijvingen
            </div>
            <p className="text-3xl font-bold text-gray-900" data-testid="admin-stat-total">
              {subscribers.length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-gray-500 text-sm mb-1">Nederlandstalig</div>
            <p className="text-3xl font-bold text-gray-900">
              {subscribers.filter((s) => s.language === 'nl').length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-gray-500 text-sm mb-1">Engelstalig</div>
            <p className="text-3xl font-bold text-gray-900">
              {subscribers.filter((s) => s.language === 'en').length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading && subscribers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">Lijst aan het laden…</div>
          ) : subscribers.length === 0 ? (
            <div className="p-12 text-center text-gray-500" data-testid="admin-empty">
              Nog geen inschrijvingen. Zodra iemand zich inschrijft via je site verschijnt die hier.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-6 py-3 font-semibold">E-mailadres</th>
                    <th className="px-6 py-3 font-semibold">Taal</th>
                    <th className="px-6 py-3 font-semibold">Toestemming</th>
                    <th className="px-6 py-3 font-semibold">Ingeschreven op</th>
                    <th className="px-6 py-3 font-semibold text-right">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscribers.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50" data-testid={`admin-row-${sub.id}`}>
                      <td className="px-6 py-4 font-medium text-gray-900 break-all">{sub.email}</td>
                      <td className="px-6 py-4 text-gray-600 uppercase">{sub.language || '—'}</td>
                      <td className="px-6 py-4">
                        {sub.consent_given ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            ✓ Ja
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{formatDate(sub.subscribed_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(sub)}
                          disabled={deleting === sub.id}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                          data-testid={`admin-delete-${sub.id}`}
                          title="Inschrijver verwijderen"
                        >
                          <Trash2 className="w-4 h-4" />
                          {deleting === sub.id ? 'Bezig…' : 'Verwijderen'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 mt-6 text-center">
          Je sessie wordt actief gehouden in deze browser. Klik op "Uitloggen" om hem te beëindigen.
        </p>
      </div>
    </div>
  );
};

export default AdminNewsletterPage;
